import React, { useEffect, useState, useMemo } from "react";
import { RootState } from "~/common/Store/models/RootReadonly";
import { User } from "~/common/Store/models/User";
import { getTotalRatings, updateHelpfulReview, recordHelpfulVoteOnStorage, getUserExistingHelpfulVotesFromStorage, allRatings, allVariations, filterReviews, sortReviews } from "./CustomerReviewHelper";
import { CustomerReviews, FilteredBy, MediaIdentifier } from "./reviewModels";
import { ProductDetailGroupingSelect } from "~/ProductDetails/ProductDetailPageResult";
import TextReviewsSection from "./components/TextReviewsSection";
import ReviewAggregationToolbar from "./components/ReviewAggregationToolbar";
import ReviewsOverviewV2 from "./components/ReviewsOverview";
import ProductReviewMediaModal from "./components/ProductReviewMediaModal";
import LeaveAReview from "./components/LeaveAReview";
import ProductReviewsDescription from "~/ProductReviews/ProductReviewsHeading/ProductReviewsDescription/ProductReviewsDescription";
import { homepage } from "~/common/Constants/Constants";
import { withUserAndChat } from "~/common/Store/connectUserAndChat";
import orderBy from "lodash/orderBy";
import LeaveAReviewAndMediaThumbnailsWrapper from "./components/LeaveAReview/LeaveAReviewAndMediaThumbnailsWrapper";
import(/* webpackChunkName: "modals-standalone" */"~/bootstrap/less/modals-standalone.less");
import(/* webpackChunkName: "product-reviews" */"~/less/product/product-reviews.less");
interface ReviewsProps {
  itemNumber: string;
  brand: string;
  reviews: CustomerReviews;
  user: User;
  mounted: boolean;
  isVirtualGroupMaster?: boolean;
  productDetailGroupingSelect?: ProductDetailGroupingSelect[];
  handleVirtualGroupingSelectChange?: (itemNumber: string) => string;
  isEverliving?: boolean;
  usePagination?: boolean;
  isProductReviewPage?: boolean;
  vgMasterOriginalItemNumber?: string;
  image: string;
  description: string;
  productLink?: string;
  extendedDescription?: string;
  customMetaHeader?: string;
  stickyHeaderCollapsed?: boolean;
}
export const mediaReviewType = {
  PHOTO: 0,
  VIDEO: 1
};
const ReviewsV2 = (props: ReviewsProps) => {
  const {
    itemNumber,
    brand,
    reviews,
    user,
    isVirtualGroupMaster = false,
    productDetailGroupingSelect = [],
    handleVirtualGroupingSelectChange = undefined,
    isEverliving,
    usePagination = false,
    isProductReviewPage = false,
    vgMasterOriginalItemNumber = "",
    image,
    description,
    productLink = homepage,
    extendedDescription = "",
    customMetaHeader = "",
    stickyHeaderCollapsed
  } = props;
  const getEncodedItemNumber = (itemNumber: string) => {
    if (itemNumber) {
      // Forward-slash is not one of the chars that is automatically encoded/decoded for reviews links
      return itemNumber.toUpperCase().replace("/", "*");
    }
    return itemNumber;
  };
  const getReviewLink = () => {
    if (!isProductReviewPage) {
      const correctedItemNumber = isVirtualGroupMaster ? vgMasterOriginalItemNumber : itemNumber;
      const encodedItemNumber = getEncodedItemNumber(correctedItemNumber);
      return `/product-reviews/${encodedItemNumber}.html`;
    }
    return "";
  };
  const hasProductReviews = reviews.productReviewDetails?.length > 0;
  const [activeMediaIndex, setActiveMediaIndex] = useState(-1);
  const [currentReviewUpvotes, setCurrentReviewUpvotes] = useState<Record<number, boolean>>({});
  const allProductReviews = useMemo(() => getTotalRatings(reviews.productReviewDetails), [reviews.productReviewDetails]);
  const [currentPage, setCurrentPage] = useState(1);
  const mediaReviews = useMemo(() => reviews.productReviewDetails.flatMap(p => p.mediaReviews), [reviews.productReviewDetails]);
  const mediaReviewsOrdered = useMemo(() => orderBy([...mediaReviews], mediaReview => new Date(mediaReview.dateSubmitted), "desc"), [mediaReviews]);
  const defaultFilteredBy: FilteredBy = {
    rating: allRatings,
    variation: {
      type: "",
      value: allVariations
    }
  };
  const [filteredBy, setFilteredBy] = useState<FilteredBy>(defaultFilteredBy);
  const defaultReviewSortBy = isProductReviewPage ? "date" : "most_helpful";
  const [reviewSortBy, setReviewSortBy] = useState(defaultReviewSortBy);
  const totalNumberOfReviews = getTotalRatings(reviews.productReviewDetails).length;
  const shouldUseCutoff = totalNumberOfReviews >= 40 && !usePagination;
  const textReviewCutoff = shouldUseCutoff ? 20 : totalNumberOfReviews;
  useEffect(() => {
    const userIndex = Number(user?.index) || 0;
    if (userIndex > 0) {
      const userVotesMap = getUserExistingHelpfulVotesFromStorage(userIndex);
      setCurrentReviewUpvotes(userVotesMap);
    }
  }, [user.index]);
  const filterByChangeHandler = (filteredBy = defaultFilteredBy): void => {
    setCurrentPage(1);
    setFilteredBy(filteredBy ?? defaultFilteredBy);
  };
  const sortByChangeHandler = (sortBy: string) => {
    setReviewSortBy(sortBy);
  };
  const ratingClickHandler = (rating: number) => {
    setFilteredBy(prev => ({
      ...prev,
      rating: rating.toString()
    }));
  };
  const handleSelectMediaElement = (newMedia: MediaIdentifier | undefined) => {
    const index = mediaReviewsOrdered.findIndex(m => m.id === newMedia?.id && m.type === newMedia?.type);
    setActiveMediaIndex(index);
  };
  const getProductReviewBySelectedMediaElement = () => {
    const mediaReview = activeMediaIndex >= 0 ? mediaReviewsOrdered[activeMediaIndex] : undefined;
    return reviews.productReviewDetails.find(p => p.mediaReviews.some(m => m.id === mediaReview?.id && m.type === mediaReview?.type));
  };
  const handleNext = () => {
    setActiveMediaIndex((activeMediaIndex + 1) % mediaReviews.length);
  };
  const handlePrev = () => {
    setActiveMediaIndex((activeMediaIndex - 1 + mediaReviews.length) % mediaReviews.length);
  };
  const addUserUpvote = (reviewId: number, upvoted: boolean) => {
    const newCurrentUpvotes = {
      ...currentReviewUpvotes
    };
    newCurrentUpvotes[reviewId] = upvoted;
    updateHelpfulReview(reviewId, upvoted);
    recordHelpfulVoteOnStorage(reviewId, Number(user?.index) || 0, upvoted);
    setCurrentReviewUpvotes(newCurrentUpvotes);
  };
  const hasMediaReviews = mediaReviewsOrdered?.length > 0;
  const modalProductReview = getProductReviewBySelectedMediaElement();
  const sortedReviews = sortReviews([...allProductReviews], reviewSortBy);
  const sortedAndFilteredReviews = filterReviews([...sortedReviews], filteredBy);
  return <>
      {isProductReviewPage ? <div className="box mt-4">
          <ProductReviewsDescription description={description} extendedDescription={extendedDescription} image={image} itemNumber={itemNumber} link={productLink} customMetaHeader={customMetaHeader} />
          <ReviewsOverviewV2 {...props} filteredBy={filteredBy} onRatingClick={ratingClickHandler} isProductReviewPage />
        </div> : null}
      <div className="review-section clears">
        {hasProductReviews ? <>
            {!isProductReviewPage && <h3 id="reviews" className="spam-reviews-option-0 text-xl leading-tight my-4 ml-4 xs:ml-0" tabIndex={-1}>
                Customer Reviews
              </h3>}
            <div id="review-section" className={`main-col ${isProductReviewPage ? "w-full" : ""}`} style={{
          scrollMarginTop: "100px"
        }}>
              {!isProductReviewPage && <ReviewsOverviewV2 {...props} className="block lt:hidden" filteredBy={filteredBy} onRatingClick={ratingClickHandler} />}
              <div className={`side-col aside ${!isProductReviewPage ? "mt-4 lt:mt-0" : ""}`}>
                <LeaveAReviewAndMediaThumbnailsWrapper {...{
              ...props,
              handleSelectMediaElement,
              hasMediaReviews,
              mediaReviewsOrdered
            }} />
                <ProductReviewMediaModal productReviewDetails={modalProductReview} brand={brand} selectedMedia={activeMediaIndex >= 0 ? mediaReviewsOrdered[activeMediaIndex] : undefined} user={user} handleSelectMediaElement={handleSelectMediaElement} handleNext={handleNext} handlePrev={handlePrev} currentReview={activeMediaIndex + 1} totalReviews={mediaReviews.length} addUserUpvote={addUserUpvote} userUpvoted={!!currentReviewUpvotes[modalProductReview?.reviewId ?? 0]} userVoted={currentReviewUpvotes[modalProductReview?.reviewId ?? 0] !== undefined} allMediaReviews={mediaReviewsOrdered} />
              </div>
              <section className="media-list grid-main" aria-labelledby="reviews">
                {!isProductReviewPage && <ReviewsOverviewV2 {...props} className="hidden lt:block" filteredBy={filteredBy} onRatingClick={ratingClickHandler} />}
                <ReviewAggregationToolbar reviewLink={getReviewLink()} {...props} isProductReviewPage={isProductReviewPage} allProductReviews={allProductReviews} filteredBy={filteredBy} filterByChangeHandler={filterByChangeHandler} sortByChangeHandler={sortByChangeHandler} stickyHeaderCollapsed={stickyHeaderCollapsed} />
                <TextReviewsSection addUserUpvote={addUserUpvote} currentReviewUpvotes={currentReviewUpvotes} handleSelectMediaElement={handleSelectMediaElement} productReviewDetails={sortedAndFilteredReviews.slice(0, textReviewCutoff)} user={user} usePagination={usePagination} currentPage={currentPage} setCurrentPage={setCurrentPage} />
              </section>
            </div>
          </> : !isEverliving && <LeaveAReview user={user} itemNumber={itemNumber} displayExtended productDetailGroupingSelect={productDetailGroupingSelect} handleVirtualGroupingSelectChange={handleVirtualGroupingSelectChange} isVirtualGroupMaster={isVirtualGroupMaster} isProductReviewPage={isProductReviewPage} image={image} description={description} productLink={productLink} />}
      </div>
    </>;
};
export const mapStateToProps = (state: RootState, ownProps: ReviewsProps) => {
  if (!ownProps.mounted) return {
    user: ownProps.user
  };
  return {
    user: state.user
  };
};
export default withUserAndChat(ReviewsV2);